/**
 * Como se le avisa a la persona que algo salio mal.
 *
 * LA REGLA UNICA DE ESTE ARCHIVO
 * ------------------------------
 * El texto del backend llega ENTERO a la pantalla. Los RPCs de la base tiran
 * `raise exception` con mensajes escritos para que los lea un vendedor parado
 * frente a un cliente:
 *
 *   "La venta excede el credito disponible del cliente (queda $ 500.000).
 *    Confirma la venta con forzar si igual queres hacerla"
 *
 * Eso no es un error tecnico: es una instruccion. Dice cuanto queda y dice como
 * seguir. Reemplazarlo por "No se pudo emitir la venta" convierte una decision
 * de treinta segundos en un llamado al que armo el sistema.
 *
 * Por eso aca no hay catalogo de mensajes propios, ni mapeo de codigos, ni
 * "traduccion" de nada. `mensajeDeError()` de `api.ts` ya sabe de donde sacar el
 * texto en cada forma de respuesta; este modulo solo lo pone en un toast.
 */
import { ErrorApi, esNoAutenticado, mensajeDeError } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

/** Cuanto texto entra en un toast antes de volverse ilegible. */
const LARGO_MAXIMO = 400;

function recortar(texto: string): string {
  return texto.length <= LARGO_MAXIMO ? texto : `${texto.slice(0, LARGO_MAXIMO - 1)}…`;
}

/**
 * El titulo cambia segun QUE clase de problema es, porque cambia que tiene que
 * hacer la persona: revisar lo que cargo, esperar, o llamar a alguien.
 */
function titulo(error: unknown): string {
  if (!(error instanceof ErrorApi)) return "No se pudo completar";
  if (error.esDeRed) return "Sin conexion con el sistema";
  if (error.sinPermiso) return "No tenes permiso";
  if (error.noAutenticado) return "Se cerro la sesion";
  if (error.estado === 404) return "No lo encontramos";
  if (error.estado >= 500) return "Problema del servidor";
  // 400 y 422 son la operacion rechazada por una regla de negocio: el titulo se
  // hace a un lado para que el protagonista sea el mensaje del backend.
  return "No se pudo completar";
}

/**
 * Muestra el error en un toast, con el mensaje del backend tal cual vino.
 *
 * Devuelve el texto que se mostro para que quien llama pueda ademas dejarlo
 * fijo en la pantalla (el credito excedido, por ejemplo, se lee dos veces: en el
 * toast y arriba del boton de confirmar).
 */
export function mostrarError(error: unknown): string {
  const mensaje = recortar(mensajeDeError(error));
  toast({
    variant: "destructive",
    title: titulo(error),
    description: mensaje,
  });
  return mensaje;
}

/**
 * Igual que `mostrarError`, pero se calla ante un 401.
 *
 * Un 401 ya dispara el aviso de sesion perdida y la vuelta al login: sumarle un
 * toast rojo por cada consulta que estaba en vuelo llena la pantalla de ruido
 * arriba de la pantalla de login.
 */
export function mostrarErrorSalvoSesion(error: unknown): string | null {
  if (esNoAutenticado(error)) return null;
  return mostrarError(error);
}

/** La contracara: una confirmacion corta de que la operacion se hizo. */
export function mostrarExito(mensaje: string, detalle?: string): void {
  toast({ variant: "success", title: mensaje, description: detalle });
}
